import { SNAPSHOT } from "./brands.generated";

/** One live brand, as snapshotted by `scripts/sync-brands.mjs`. */
export type SnapshotBrand = {
  id: string;
  name: string;
  slug: string;
  domain: string;
  /** Max € a user can earn per recommendation (backend estimate). */
  reward: number | null;
  /** `staticFile()` paths, or null when the merchant has none. */
  logo: string | null;
  hero: string | null;
  /** Every gallery photo, `hero` first, with its pixel size. */
  heroes: SnapshotImage[];
  /** Merchant-written philosophy (explorerConfig), as-is. FR only today. */
  description: string | null;
  /**
   * Measured on the logo by the sync script: `tile` = its own opaque
   * background (null when transparent), `ink` = its dominant mark colour.
   */
  palette: { tile: string | null; ink: string } | null;
};

export type SnapshotImage = { src: string; width: number; height: number };

export type Brand = SnapshotBrand & {
  /** Display name: "SAINT LAZARE" → "Saint Lazare"; short acronyms kept. */
  label: string;
  reward: number;
};

export const toLabel = (name: string) =>
  name.length > 3 && name === name.toUpperCase()
    ? name.toLowerCase().replace(/(^|[\s-])\p{L}/gu, (c) => c.toUpperCase())
    : name;

/**
 * A snapshot brand the videos can show: logo, hero photo and a valued
 * reward. Also used by the render pipeline (`server/lib/`) to decide which
 * merchant videos exist.
 */
export const isLive = (
  b: SnapshotBrand,
): b is SnapshotBrand & { reward: number } =>
  b.logo !== null && b.hero !== null && b.reward !== null && b.reward > 0;

/** Live brands with a logo, a hero and a valued reward, in backend order. */
export const BRANDS: Brand[] = SNAPSHOT.filter(isLive).map((b) => ({
  ...b,
  label: toLabel(b.name),
}));

/**
 * The brand the story follows (the black tote in the chat, the order
 * confirmation and the story). Falls back to the first live brand if it
 * ever leaves the directory.
 */
export const STORY_BRAND: Brand =
  BRANDS.find((b) => b.slug === "saint-lazare") ?? BRANDS[0];

/** What the story earns in step 3. Never above the brand's live reward. */
export const STORY_GAIN = Math.min(5, STORY_BRAND.reward);
